"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type WheelEvent,
} from "react";
import { usePathname } from "next/navigation";
import { MenuProvider } from "@/lib/menu/MenuProvider";
import { ToastProvider } from "@/lib/toast";
import { DetailsSidebar } from "./DetailsSidebar";
import { DmBar } from "./DmBar";
import { MainFrame } from "./MainFrame";
import { ProfileBar } from "./ProfileBar";
import { ServerRail } from "./ServerRail";
import { TopBar } from "./TopBar";

/* Columns + card chrome that sit around the main frame, read live so the
   mobile cutoff follows the CSS metrics instead of a hard-coded number.
   Measured with a probe element — the vars are clamp()/calc() now, which
   getComputedStyle can't give as px. */
function chromeWidth(): number {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;visibility:hidden;height:0;pointer-events:none";
  document.body.appendChild(probe);
  const v = (name: string) => {
    probe.style.width = `var(${name})`;
    return probe.getBoundingClientRect().width;
  };
  const w =
    v("--rail-w") +
    v("--dmbar-w") +
    v("--side-w") +
    v("--card-bw") +
    v("--matte-l") +
    v("--matte-r");
  probe.remove();
  return w;
}

/* Mobile when the main frame can't keep at least half the screen. null until
   measured so the wrong layout never flashes. */
function useMobileLayout() {
  const [mobile, setMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const check = () =>
      setMobile(window.innerWidth - chromeWidth() < window.innerWidth / 2);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return mobile;
}

/* Horizontal pager physics, modeled on iOS paging + Embla's drag tracker:
   - 8px slop + direction lock before claiming the gesture
   - velocity sampled over a sliding 120ms window (stale lifts = no flick)
   - flick (>300px/s, iOS' ~250-300pt/s) assists only past 25% travel —
     under that, release position alone decides (peeks snap back)
   - iOS logarithmic rubber band past the edges
   - settle is a spring seeded with release velocity, so flicks carry momentum
     instead of restarting into a fixed-duration ease */
const FLICK_V = 300; // px/s
const SPRING_K = 420;
const SPRING_Z = 0.9; // damping ratio — slightly under critical

/* Lets nav surfaces (DM rows, rail pings) slide the mobile pager to a
   page — 0 = nav, 1 = chat. No-op on desktop where there's no pager. */
const ChatNavCtx = createContext<{
  goToPage(p: number): void;
  /* dms closed from the bar — the rows and the rail pings both read
     this so they stay in sync */
  closedDms: ReadonlySet<string>;
  closeDm(id: string): void;
}>({ goToPage: () => {}, closedDms: new Set(), closeDm: () => {} });
export function useChatNav() {
  return useContext(ChatNavCtx);
}

/* which conversation surface the shell is showing — drives the details
   sidebar's body (dms blank, gc/notes get the member list) */
export type ChatKind =
  | "dm"
  | "gc"
  | "notes"
  | "dashboard"
  | "friends"
  | "server";

interface Drag {
  x0: number;
  y0: number;
  base: number;
  origin: number; // page index at gesture start
  locked: boolean | null;
  samples: { x: number; t: number }[];
}

function rubber(d: number, dim: number): number {
  return (1 - 1 / ((d * 0.55) / dim + 1)) * dim;
}

/* kind comes from the route now — the shell lives in the (chat) route
   group's layout so it stays mounted across navigations and the mobile
   pager keeps animating instead of hard-remounting on every change */
function kindFrom(pathname: string): ChatKind {
  if (pathname.startsWith("/app/d/")) return "dm";
  if (pathname.startsWith("/app/g/")) return "gc";
  if (pathname.startsWith("/app/n/")) return "notes";
  if (pathname.startsWith("/app/friends")) return "friends";
  /* server routes live outside /app — /s/<server>/<channel> */
  if (pathname.startsWith("/s/")) return "server";
  return "dashboard";
}

export function ChatShell() {
  const pathname = usePathname();
  const kind = kindFrom(pathname);
  const mobile = useMobileLayout();
  const [detailsOpen, setDetailsOpen] = useState(false);
  /* xl+ the sidebar is inline — collapse squeezes it to zero width instead
     of opening the overlay/sheet that smaller breakpoints use */
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [closedDms, setClosedDms] = useState<Set<string>>(new Set());
  const closeDm = (id: string) =>
    setClosedDms((s) => new Set(s).add(id));
  const track = useRef<HTMLDivElement>(null);
  const pos = useRef<number | null>(null); // px, 0 = nav / -w = chat
  const page = useRef(1);
  const drag = useRef<Drag | null>(null);
  const anim = useRef<number | null>(null);
  const wheelAt = useRef(0);
  const suppressClick = useRef(false);
  const close = () => setDetailsOpen(false);

  /* --side-off: how far the composer's right edge sits from the viewport
     edge — the details sidebar's width when it's inline, 0 when it
     collapses. composer menus dock their right edge to it */
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--side-off",
      sideCollapsed || window.innerWidth < 1280 ? "0px" : "var(--side-w)",
    );
  }, [sideCollapsed, mobile]);

  /* One toggle, three behaviors: inline collapse on xl+, pullout overlay
     on smaller desktop, pager slide to page 2 on mobile. */
  const toggleDetails = () => {
    if (mobile) goTo(page.current === 2 ? 1 : 2);
    else if (window.innerWidth >= 1280) setSideCollapsed((c) => !c);
    else setDetailsOpen((o) => !o);
  };

  function curPos(): number {
    const el = track.current;
    if (pos.current === null) pos.current = el ? -el.clientWidth : 0;
    return pos.current;
  }

  function render(px: number) {
    const el = track.current;
    if (!el) return;
    pos.current = px;
    el.style.transform = `translateX(${px}px)`;
  }

  function cancelAnim() {
    if (anim.current !== null) {
      cancelAnimationFrame(anim.current);
      anim.current = null;
    }
  }

  function animateTo(target: number, v0 = 0) {
    cancelAnim();
    const stiffness = SPRING_K;
    const damping = 2 * SPRING_Z * Math.sqrt(SPRING_K);
    let x = curPos();
    let v = v0;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      v += (-stiffness * (x - target) - damping * v) * dt;
      x += v * dt;
      render(x);
      if (Math.abs(x - target) < 0.5 && Math.abs(v) < 20) {
        render(target);
        anim.current = null;
        return;
      }
      anim.current = requestAnimationFrame(step);
    };
    anim.current = requestAnimationFrame(step);
  }

  function goTo(p: number) {
    page.current = p;
    setDetailsOpen(p === 2);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    animateTo(-p * (track.current?.clientWidth ?? 0));
  }

  useEffect(() => cancelAnim, []);

  /* Once a horizontal drag owns the gesture, preventDefault touchmove so the
     browser can't steal it for scrolling/overscroll (which fires
     pointercancel and yanks the page back mid-drag). Must be a native
     non-passive listener — React's onTouchMove is passive. */
  useEffect(() => {
    const el = track.current;
    if (!mobile || !el) return;
    const stop = (e: TouchEvent) => {
      if (drag.current?.locked) e.preventDefault();
    };
    el.addEventListener("touchmove", stop, { passive: false });
    return () => el.removeEventListener("touchmove", stop);
  }, [mobile]);

  function onDown(e: PointerEvent<HTMLDivElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    /* Tapping non-editable chrome while the keyboard is up dismisses it at
       touch-down — otherwise the browser consumes the tap during the
       viewport resize and the click never lands. */
    const ae = document.activeElement;
    const t = e.target as HTMLElement;
    if (
      e.pointerType !== "mouse" &&
      ae instanceof HTMLElement &&
      ae.matches("input, textarea, [contenteditable]") &&
      !t.closest("input, textarea, [contenteditable]")
    ) {
      ae.blur();
    }
    /* grabbing mid-settle steals the animation — the drag resumes from the
       live position like a native scroll view */
    cancelAnim();
    const base = curPos();
    const w = track.current?.clientWidth ?? 1;
    drag.current = {
      x0: e.clientX,
      y0: e.clientY,
      base,
      origin: Math.max(0, Math.min(2, Math.round(-base / w))),
      locked: null,
      samples: [],
    };
  }

  function onMove(e: PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    const el = track.current;
    if (!d || !el) return;

    const raw = e.clientX - d.x0;
    if (d.locked === null) {
      const dy = e.clientY - d.y0;
      if (Math.abs(raw) < 8 && Math.abs(dy) < 8) return;
      /* vertical gesture wins — hand it back to native scrolling */
      d.locked = Math.abs(raw) > Math.abs(dy);
      if (!d.locked) {
        drag.current = null;
        return;
      }
      /* capture only once it's a page drag, so plain taps still click */
      e.currentTarget.setPointerCapture(e.pointerId);
      /* leaving the chat dismisses the keyboard — no point keeping it up
         on the nav page */
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }

    d.samples.push({ x: e.clientX, t: e.timeStamp });
    while (d.samples.length > 2 && e.timeStamp - d.samples[0].t > 120) {
      d.samples.shift();
    }

    const w = el.clientWidth;
    let px = d.base + raw;
    if (px < -2 * w) px = -2 * w - rubber(-px - 2 * w, w * 0.4);
    else if (px > 0) px = rubber(px, w * 0.4);
    render(px);
  }

  function releaseVelocity(d: Drag, e: PointerEvent<HTMLDivElement>): number {
    const s = d.samples;
    if (s.length < 2) return 0;
    const last = s[s.length - 1];
    const dt = last.t - s[0].t;
    /* finger rested before lifting — treat as no flick */
    if (dt <= 0 || e.timeStamp - last.t > 80) return 0;
    return ((last.x - s[0].x) / dt) * 1000;
  }

  function onUp(e: PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.locked) return;
    const w = track.current?.clientWidth ?? 1;
    const v = releaseVelocity(d, e);
    const moved = curPos() - d.base; // signed px traveled this gesture
    const frac = Math.abs(moved) / w;
    let target = d.origin;
    if (moved !== 0) {
      /* Position decides, velocity only assists: past ~a third always
         commits, and a flick only turns a page you already pulled a
         quarter of the way out. Peeks under that always snap back. */
      const toward = Math.max(
        0,
        Math.min(2, d.origin - Math.sign(moved)),
      );
      if (
        frac >= 0.35 ||
        (frac > 0.25 &&
          Math.abs(v) > FLICK_V &&
          Math.sign(v) === Math.sign(moved))
      ) {
        target = toward;
      }
    }
    page.current = target;
    setDetailsOpen(target === 2);
    suppressClick.current = Math.abs(e.clientX - d.x0) > 6;
    animateTo(-target * w, v);
  }

  function onCancel() {
    drag.current = null;
    animateTo(-page.current * (track.current?.clientWidth ?? 0));
  }

  /* Trackpads / mice with horizontal wheels emit deltaX — treat a dominant
     horizontal scroll as one page turn, throttled so a swipe flips once. */
  function onWheel(e: WheelEvent<HTMLDivElement>) {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    const now = e.timeStamp;
    if (now - wheelAt.current < 350) return;
    wheelAt.current = now;
    const target = Math.max(
      0,
      Math.min(2, page.current + (e.deltaX > 0 ? 1 : -1)),
    );
    if (target !== page.current) goTo(target);
  }

  if (mobile === null) {
    return <div className="h-dvh w-full bg-shell" />;
  }

  const shell = mobile ? (
      <div className="relative h-dvh w-full overflow-hidden bg-shell">
        <div
          ref={track}
          className="flex h-full w-full touch-pan-y select-none will-change-transform"
          style={{ transform: "translateX(-100%)" }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onCancel}
          onDragStart={(e) => e.preventDefault()}
          onWheel={onWheel}
          onClickCapture={(e) => {
            if (suppressClick.current) {
              e.preventDefault();
              e.stopPropagation();
            }
            suppressClick.current = false;
          }}
        >
          <div
            className="relative flex h-full w-full shrink-0 flex-col"
            style={{ "--rail-w": "72px" } as CSSProperties}
          >
            <TopBar mobile />
            <div className="flex min-h-0 flex-1">
              <ServerRail mobile />
              <DmBar mobile />
            </div>
            <ProfileBar mobile />
          </div>
          <div className="flex h-full w-full shrink-0">
            <MainFrame
              mobile
              onToggleDetails={toggleDetails}
              detailsOn={detailsOpen}
              kind={kind}
            />
          </div>
          {/* page 2 — details sidebar, slides in from the right as a
              full pager pane instead of a bottom sheet */}
          <div className="flex h-full w-full shrink-0">
            <DetailsSidebar
              pane
              open={detailsOpen}
              onClose={() => goTo(1)}
              kind={kind}
            />
          </div>
        </div>
      </div>
    ) : (
    <div className="flex h-dvh w-full flex-col bg-shell">
      <TopBar />
      <div className="relative flex min-h-0 flex-1 overflow-hidden bg-shell">
        <ServerRail />

        <div className="relative flex min-w-0 flex-1 flex-col">
          {/* fills the card's top-left corner box with chrome so the rim
              border's arc reads as part of the rail+topbar frame instead
              of a black notch */}
          <div
            aria-hidden
            className="absolute left-0 top-0 h-[var(--card-r)] w-[var(--card-r)] bg-chrome"
          />
          <div className="chat-card">
            <DmBar />
            <MainFrame
              onToggleDetails={toggleDetails}
              detailsOn={detailsOpen || !sideCollapsed}
              kind={kind}
            />
            <DetailsSidebar
              open={detailsOpen}
              collapsed={sideCollapsed}
              onClose={close}
              kind={kind}
            />
          </div>
        </div>

        {detailsOpen ? (
          <button
            type="button"
            aria-label="Close details"
            onClick={close}
            className="absolute inset-0 z-20 bg-scrim animate-fade xl:hidden"
          />
        ) : null}

        <ProfileBar />
      </div>
    </div>
  );

  /* ChatNavCtx outside MenuProvider — menu popups portal through the
     provider, so rows/pills inside a sheet can still slide the pager.
     ToastProvider wraps both so menu contents can fire toasts too. */
  return (
    <ChatNavCtx.Provider
      value={{ goToPage: goTo, closedDms, closeDm }}
    >
      <ToastProvider mobile={mobile}>
        <MenuProvider mobile={mobile}>{shell}</MenuProvider>
      </ToastProvider>
    </ChatNavCtx.Provider>
  );
}
